import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, PenLine } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CONTROL_RESULT, fmtDate, fmtMoney, INVOICE_STATUS, PO_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, PageHeader, ToneChip } from "../../components/common";
import { ApprovalCard } from "../../components/panels";
import { DemoBanner, FinActions, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const FIELD_LABELS = {
  proveedor: "Proveedor", numero: "Número", fecha: "Fecha", total: "Total", impuestos: "Impuestos",
  vencimiento: "Vencimiento", iban: "IBAN", referencia_orden: "Referencia de orden", proyecto: "Proyecto", partida: "Partida",
};

function CreditNoteDialog({ inv, open, onClose, onDone }) {
  const [form, setForm] = useState({ amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/finanzas/invoices/${inv.id}/credit-note`, { amount: Number(form.amount), reason: form.reason });
      toast.success("Abono registrado — la factura original se conserva");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="credit-note-dialog">
        <DialogHeader><DialogTitle>Registrar abono sobre {inv.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Importe del abono (€) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cn-amount" /></div>
          <div><Label>Motivo *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cn-reason" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || !Number(form.amount) || !form.reason.trim()} data-testid="cn-submit">
            {busy ? "Registrando…" : "Registrar abono"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FacturaDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { map } = useUsers();
  const { user } = useAuth();
  const canWrite = (user?.roles || []).some((r) => r !== "VIEWER");
  const [cnOpen, setCnOpen] = useState(false);
  const [correct, setCorrect] = useState(null); // {key, value}
  const { data, refetch } = useQuery({
    queryKey: ["fin-invoice", id],
    queryFn: async () => (await api.get(`/finanzas/invoices/${id}`)).data,
  });
  if (!data) return null;
  const { invoice: inv, actions, supplier, purchase_order, receipts, exceptions, approvals, disputes, credit_notes } = data;
  const fields = inv.extracted_fields || {};

  const act = async (action, payload) => {
    await api.post(`/finanzas/invoices/${inv.id}/action`, { action, ...payload });
    toast.success("Acción ejecutada y registrada");
    refetch();
    qc.invalidateQueries({ queryKey: ["fin-invoices"] });
  };

  const fieldAction = async (key, action, value) => {
    try {
      await api.post(`/finanzas/invoices/${inv.id}/fields/${key}`, { action, value });
      toast.success(action === "corregir" ? "Campo corregido — valor inicial conservado" : "Campo validado");
      setCorrect(null);
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="factura-detail">
      <PageHeader title={`Factura ${inv.invoice_number} — ${inv.supplier_name}`} subtitle={`Emitida ${fmtDate(inv.issue_date)} · vence ${fmtDate(inv.due_date)} · origen: ${inv.source}`} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Chip map={INVOICE_STATUS} value={inv.status} testid="inv-detail-status" />
        {inv.duplicate_status !== "no_duplicado" && <ToneChip label={`Duplicado: ${inv.duplicate_status}`} tone="danger" testid="inv-dup-chip" />}
        {inv.blocked_reason && <ToneChip label={inv.blocked_reason} tone="danger" testid="inv-blocked-chip" />}
        {purchase_order && <Link to={`/finanzas/ordenes/${purchase_order.id}`} className="ts-chip ts-chip-info" data-testid="inv-po-link">{purchase_order.po_number} →</Link>}
      </div>
      <DemoBanner />

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="ts-card p-5" data-testid="inv-summary">
            <div className="grid gap-3 sm:grid-cols-4">
              <div><p className="ts-caption">Base</p><Money value={inv.subtotal} className="text-sm" /></div>
              <div><p className="ts-caption">Impuestos</p><Money value={inv.taxes} className="text-sm" /></div>
              <div><p className="ts-caption">Retención</p><Money value={inv.retention} className="text-sm" /></div>
              <div><p className="ts-caption">Total</p><Money value={inv.total} className="text-xl font-bold text-slate-900" /></div>
            </div>
            <p className="mt-2 text-xs text-slate-500">Partida: <span className="font-mono">{inv.budget_reference || "—"}</span> · Proyecto: {inv.project || "—"} · Método: {inv.payment_method}</p>
          </div>

          <div className="ts-card overflow-hidden" data-testid="inv-extraction">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Extracción IA — validación humana obligatoria de campos materiales</p>
            <Table>
              <TableHeader><TableRow><TableHead>Campo</TableHead><TableHead>Valor</TableHead><TableHead>Fuente</TableHead><TableHead>Confianza</TableHead><TableHead>Validación</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {Object.entries(fields).map(([key, f]) => (
                  <TableRow key={key} data-testid={`inv-field-${key}`} className={f.validation_status === "pendiente" && f.material ? "bg-amber-50/40" : ""}>
                    <TableCell className="text-xs font-medium">{FIELD_LABELS[key] || key}{f.material && <span className="ml-1 text-amber-600">*</span>}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {f.value ?? "NO IDENTIFICADO"}
                      {f.corrected_value !== null && f.corrected_value !== undefined && <span className="ml-1 text-amber-700" title={`Inicial: ${f.initial_value}`}>(corregido)</span>}
                    </TableCell>
                    <TableCell className="max-w-40 truncate text-xs text-slate-500" title={f.source_text}>{f.source_text}</TableCell>
                    <TableCell>
                      {f.confidence !== null && f.confidence !== undefined && (
                        <span className={`font-mono text-xs font-semibold ${f.confidence < 0.7 ? "text-red-600" : f.confidence < 0.85 ? "text-amber-600" : "text-emerald-600"}`}>{f.confidence}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ToneChip
                        tone={f.validation_status === "validado" ? "success" : f.validation_status === "corregido" ? "warning" : f.validation_status === "no_aplicable" ? "muted" : "warning"}
                        label={{ validado: "Validado", corregido: "Corregido", no_aplicable: "No aplicable", pendiente: "Pendiente" }[f.validation_status]}
                        testid={`inv-field-status-${key}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {f.validation_status === "pendiente" && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => fieldAction(key, "validar")} data-testid={`inv-field-validate-${key}`}><Check className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setCorrect({ key, value: f.value ?? "" })} data-testid={`inv-field-correct-${key}`}><PenLine className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="ts-card overflow-hidden" data-testid="inv-controls">
            <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Controles de matching ({inv.controls?.length || 0})</p>
            {inv.controls?.length ? (
              <div className="divide-y divide-slate-100">
                {inv.controls.map((c, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 px-5 py-2.5" data-testid={`inv-control-${c.control}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{c.control}</p>
                      <p className="text-xs text-slate-500">{c.explanation}{c.action_required ? ` — acción: ${c.action_required}` : ""}</p>
                    </div>
                    <Chip map={CONTROL_RESULT} value={c.result} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-5 py-6 text-center text-sm text-slate-500">Matching no ejecutado todavía. Valida los campos materiales y ejecuta el matching.</p>
            )}
          </div>

          {disputes.length > 0 && (
            <div className="ts-card p-5" data-testid="inv-disputes">
              <p className="mb-2 font-display text-sm font-semibold">Disputas</p>
              {disputes.map((d) => (
                <div key={d.id} className="mb-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm" data-testid={`dispute-${d.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-red-900">{d.reason}</span>
                    <ToneChip label={d.status} tone={d.status === "abierta" ? "danger" : "muted"} />
                  </div>
                  <p className="mt-1 text-xs text-red-700">Importe disputado: {fmtMoney(d.amount)} · responsable: {userName(map, d.owner_id)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="ts-card p-5" data-testid="inv-actions-card">
            <h3 className="mb-3 font-display text-sm font-semibold">Acciones controladas</h3>
            <FinActions actions={actions} onAct={act} testid="inv-action" />
            {!canWrite && <p className="text-xs text-slate-500" data-testid="inv-readonly-note">Tu perfil es de solo lectura: puedes consultar la factura, pero no ejecutar acciones.</p>}
            {canWrite && <Button size="sm" variant="outline" className="mt-2" onClick={() => setCnOpen(true)} data-testid="inv-credit-note-button">Registrar abono</Button>}
          </div>
          {supplier && (
            <div className="ts-card p-5" data-testid="inv-supplier">
              <p className="ts-caption">Proveedor</p>
              <Link to={`/finanzas/proveedores?supplier=${supplier.id}`} className="text-sm font-medium text-blue-700 hover:underline">{supplier.name}</Link>
              <p className="mt-1 text-xs text-slate-500">NIF {supplier.tax_id} · {supplier.payment_terms}</p>
            </div>
          )}
          {exceptions.length > 0 && (
            <div className="ts-card p-5" data-testid="inv-exceptions">
              <h3 className="mb-2 font-display text-sm font-semibold">Excepciones</h3>
              {exceptions.map((e) => (
                <div key={e.id} className="mb-2 rounded-sm border border-red-200 bg-red-50 p-2 text-xs" data-testid={`inv-exc-${e.id}`}>
                  <span className="font-semibold text-red-800">{e.type}</span> — {e.description}
                  <p className="mt-1 text-red-600">Estado: {e.status} · responsable: {userName(map, e.owner_id)}</p>
                </div>
              ))}
              <Link to="/finanzas/excepciones" className="text-xs font-medium text-blue-700 hover:underline">Gestionar en Excepciones →</Link>
            </div>
          )}
          {credit_notes.length > 0 && (
            <div className="ts-card p-5" data-testid="inv-credit-notes">
              <p className="mb-2 font-display text-sm font-semibold">Abonos</p>
              {credit_notes.map((n) => (
                <p key={n.id} className="text-xs text-slate-600" data-testid={`credit-note-${n.id}`}>{n.number} — {fmtMoney(n.amount)} · {n.reason}</p>
              ))}
            </div>
          )}
          {approvals.map((a) => <ApprovalCard key={a.id} appr={a} me={null} onChanged={refetch} />)}
        </div>
      </div>

      <Dialog open={!!correct} onOpenChange={() => setCorrect(null)}>
        <DialogContent data-testid="field-correct-dialog">
          <DialogHeader><DialogTitle>Corregir campo «{FIELD_LABELS[correct?.key] || correct?.key}»</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">El valor inicial extraído por IA se conserva como referencia de auditoría.</p>
            <div><Label>Valor corregido</Label><Input value={correct?.value ?? ""} onChange={(e) => setCorrect({ ...correct, value: e.target.value })} data-testid="field-correct-input" /></div>
            <Button className="w-full" onClick={() => fieldAction(correct.key, "corregir", correct.value)} data-testid="field-correct-confirm">Guardar corrección</Button>
          </div>
        </DialogContent>
      </Dialog>
      <CreditNoteDialog inv={inv} open={cnOpen} onClose={() => setCnOpen(false)} onDone={() => { setCnOpen(false); refetch(); }} />
    </div>
  );
}
